import { Head, Link, router } from '@inertiajs/react'
import { useState } from 'react'
import SuperAdminLayout from '../_SuperAdminLayout'

interface Tenant { id: number; name: string; slug: string; status: string }
interface Backup {
  id: number
  taken_at_display: string
  size_display: string
  record_count: number
  status: string
  checksum: string | null
  error_message: string | null
}

interface Props { tenant: Tenant; backups: Backup[]; retention_per_tenant: number }

export default function BackupsIndex({ tenant, backups, retention_per_tenant }: Props) {
  const [restoreInPlaceFor, setRestoreInPlaceFor] = useState<number | null>(null)
  const [restoreAsNewFor, setRestoreAsNewFor] = useState<number | null>(null)
  const [confirmSlug, setConfirmSlug] = useState('')
  const [newName, setNewName] = useState('')
  const [newSlug, setNewSlug] = useState('')

  const createBackup = () => router.post(`/super_admin/tenants/${tenant.id}/backups`)
  const deleteBackup = (id: number) => {
    if (!confirm('Delete this backup? This cannot be undone.')) return
    router.delete(`/super_admin/tenants/${tenant.id}/backups/${id}`)
  }
  const downloadUrl = (id: number) => `/super_admin/tenants/${tenant.id}/backups/${id}`

  const submitInPlace = () => {
    if (!restoreInPlaceFor) return
    router.post(`/super_admin/tenants/${tenant.id}/backups/${restoreInPlaceFor}/restore_in_place`, {
      confirmation_slug: confirmSlug
    })
  }

  const submitAsNew = () => {
    if (!restoreAsNewFor) return
    router.post(`/super_admin/tenants/${tenant.id}/backups/${restoreAsNewFor}/restore_as_new`, {
      new_name: newName, new_slug: newSlug
    })
  }

  return (
    <SuperAdminLayout>
      <Head title={`Backups · ${tenant.name}`} />
      <Link href={`/super_admin/tenants/${tenant.id}`} className="text-sm text-[#ED1C24] hover:underline">&larr; {tenant.name}</Link>

      <div className="flex items-start justify-between mt-2 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Backups · {tenant.name}</h1>
          <p className="text-sm text-text-muted mt-1">Automatic backups run Mondays &amp; Thursdays at 02:00 UTC. The most recent {retention_per_tenant} are kept per tenant.</p>
        </div>
        <button onClick={createBackup} className="px-3 py-2 bg-[#ED1C24] text-white rounded-lg text-sm font-semibold hover:bg-[#C7161E]">Create backup now</button>
      </div>

      <div className="bg-white border border-stone/20 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F5F5] text-text-secondary">
            <tr>
              <th className="text-left py-3 px-4 font-semibold">Taken at</th>
              <th className="text-left py-3 px-4 font-semibold">Records</th>
              <th className="text-left py-3 px-4 font-semibold">Size</th>
              <th className="text-left py-3 px-4 font-semibold">Status</th>
              <th className="text-right py-3 px-4 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {backups.length === 0 && (
              <tr><td colSpan={5} className="py-12 text-center text-text-muted">No backups yet.</td></tr>
            )}
            {backups.map(b => (
              <tr key={b.id} className="border-t border-stone/10">
                <td className="py-3 px-4 text-text-primary">{b.taken_at_display}</td>
                <td className="py-3 px-4 text-text-secondary">{b.record_count.toLocaleString()}</td>
                <td className="py-3 px-4 text-text-secondary">{b.size_display}</td>
                <td className="py-3 px-4">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${b.status === 'complete' ? 'bg-[#15532E]/10 text-[#15532E]' : 'bg-[#ED1C24]/10 text-[#ED1C24]'}`}>{b.status}</span>
                </td>
                <td className="py-3 px-4 text-right">
                  {b.status === 'complete' && (
                    <div className="flex justify-end gap-2">
                      <a href={downloadUrl(b.id)} className="text-xs px-2 py-1 border border-stone/30 rounded hover:bg-[#F5F5F5]">Download</a>
                      <button onClick={() => { setRestoreInPlaceFor(b.id); setConfirmSlug('') }} className="text-xs px-2 py-1 border border-stone/30 rounded hover:bg-[#F5F5F5]">Restore in place</button>
                      <button onClick={() => { setRestoreAsNewFor(b.id); setNewName(`${tenant.name} (restored)`); setNewSlug('') }} className="text-xs px-2 py-1 border border-stone/30 rounded hover:bg-[#F5F5F5]">Restore as new tenant</button>
                      <button onClick={() => deleteBackup(b.id)} className="text-xs px-2 py-1 border border-[#ED1C24] text-[#ED1C24] rounded hover:bg-[#ED1C24]/5">Delete</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {restoreInPlaceFor && (
        <Modal onClose={() => setRestoreInPlaceFor(null)} title="Restore in place — DESTRUCTIVE">
          <p className="text-sm text-text-muted">Restoring in place will <strong>wipe</strong> the current data for tenant <strong>{tenant.name}</strong> and replace it with backup contents. A safety backup will be taken first.</p>
          <p className="text-sm text-text-muted mt-2">Type the slug <code className="bg-[#F5F5F5] px-1.5 py-0.5 rounded">{tenant.slug}</code> to confirm:</p>
          <input value={confirmSlug} onChange={e => setConfirmSlug(e.target.value)} className="mt-2 w-full px-3 py-2 border border-stone/30 rounded-lg text-sm" />
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setRestoreInPlaceFor(null)} className="px-3 py-2 border border-stone/30 rounded-lg text-sm">Cancel</button>
            <button onClick={submitInPlace} disabled={confirmSlug !== tenant.slug} className="px-3 py-2 bg-[#ED1C24] text-white rounded-lg text-sm font-semibold disabled:opacity-50">Wipe & restore</button>
          </div>
        </Modal>
      )}

      {restoreAsNewFor && (
        <Modal onClose={() => setRestoreAsNewFor(null)} title="Restore as new tenant">
          <p className="text-sm text-text-muted">A brand-new isolated tenant will be created from this backup. Existing tenant <strong>{tenant.name}</strong> is untouched.</p>
          <div className="space-y-3 mt-3">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">New tenant name</label>
              <input value={newName} onChange={e => setNewName(e.target.value)} className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">New tenant slug (optional)</label>
              <input value={newSlug} onChange={e => setNewSlug(e.target.value)} placeholder="auto-generated from name" className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setRestoreAsNewFor(null)} className="px-3 py-2 border border-stone/30 rounded-lg text-sm">Cancel</button>
            <button onClick={submitAsNew} disabled={!newName.trim()} className="px-3 py-2 bg-[#ED1C24] text-white rounded-lg text-sm font-semibold disabled:opacity-50">Create new tenant from backup</button>
          </div>
        </Modal>
      )}
    </SuperAdminLayout>
  )
}

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <div className="flex items-start justify-between">
          <h2 className="text-lg font-bold text-text-primary">{title}</h2>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary">&times;</button>
        </div>
        <div className="mt-3">{children}</div>
      </div>
    </div>
  )
}
